import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../../../context/auth-context'
import { SmartAvatar } from '../../../../modules/avatar/SmartAvatar'
import { EmployeeDisplayFragment, MeetingStep, MeetingStepStatus, MeetingStepType, MeetingStepViewId, MeetingType } from '../../../../types'
import BackgroundBox from '../../BackgroundBox'
import PerFlexRow from '../../FlexRow'
import PerSpacer from '../../Spacer'
import { PerStepIndicator } from '../../StepIndicator/StepIndicator'
import { PerTag } from '../../Tag'
import PerText from '../../Text'
import './index.less'

type HeaderType = {
  employee: EmployeeDisplayFragment
  steps: MeetingStep[]
  activeStepIndex: number
  animate: boolean
  processing: boolean
  meetingType: MeetingType
  onStepClick: (step: MeetingStep, currentStepStatus?: MeetingStepStatus, callback?: () => void) => void
}

export const AppraisalStepper: FC<HeaderType> = ({ employee, steps, activeStepIndex, animate, processing, meetingType, onStepClick }) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  if (!steps || !steps.length) {
    return null
  }
  const isReviewer = user.employeeId !== employee.id

  // Override Name of New Targets Step for Appraisal Update Meeting
  const generateStepName = (step: MeetingStep): string => {
    const isAppraisalUpdate = meetingType === MeetingType.AppraisalUpdate
    const isNewTargetsStep = step.viewId === MeetingStepViewId.NewTargets

    if (isAppraisalUpdate && isNewTargetsStep) {
      return t('appraisalMeeting.meetingStepNamesByViewId.DevelopmentAndTargets')
    }

    return t('appraisalMeeting.meetingStepNamesByViewId.' + step.viewId)
  }

  const textSteps = steps.map((step) => generateStepName(step))
  const extraSpacing = textSteps.length <= 6

  const renderTag = (type) => {
    if (type === MeetingStepType.ManagerPrep) {
      return (
        <PerTag color="gold" icon="edit_fill">
          {t('appraisalMeeting.meetingStepper.status.preparation')}
        </PerTag>
      )
    } else if (type === MeetingStepType.Meeting) {
      return (
        <PerTag color="lime" icon="chat_fill">
          {t('appraisalMeeting.meetingStepper.status.meeting')}
        </PerTag>
      )
    } else {
      return <PerTag color="red">{`! ${t('errors.error')}`}</PerTag>
    }
  }

  return (
    <BackgroundBox color="transparent" noPadding r className="appraisal-stepper">
      <PerFlexRow justify="space-between">
        <BackgroundBox className="appraisal-stepper-left-box " noShadow>
          <SmartAvatar
            employeeId={employee.id}
            size="large"
            firstName={employee.chosenFirstName ? employee.chosenFirstName : employee.legalFirstName}
            lastName={employee.chosenLastName ? employee.chosenLastName : employee.legalLastName}
          />

          <PerSpacer ml="xxs">
            <PerText color="error" fontWeight="bold" mr={isReviewer ? 'xxs' : undefined}>
              {`${employee.chosenFirstName ? employee.chosenFirstName : employee.legalFirstName} ${
                employee.chosenLastName ? employee.chosenLastName : employee.legalLastName
              }`}
            </PerText>

            <PerText fontSize="s" color="secondary">
              {employee.currentEmployeeLevel && t(`employee.contract.level.${employee.currentEmployeeLevel}`)}
            </PerText>

            {steps[0].type !== MeetingStepType.EmployeePrep && <PerSpacer mt="xxxs">{renderTag(steps[0].type)}</PerSpacer>}
          </PerSpacer>
        </BackgroundBox>

        <BackgroundBox
          color="white75"
          className={`appraisal-stepper-right-box ${extraSpacing ? `appraisal-stepper-right-box--extra-spacing` : ''}`}
          noShadow
        >
          <PerStepIndicator
            meetingType={meetingType}
            steps={steps}
            activeIndex={activeStepIndex}
            animate={animate}
            processing={processing}
            onClick={onStepClick}
          />
        </BackgroundBox>
      </PerFlexRow>
    </BackgroundBox>
  )
}
